/* Next */
import Router, { useRouter } from 'next/router'

/* React */
import { useEffect, useMemo, useRef, useState } from 'react'

/* Redux */
import SucheRedux from './Suche.Redux'
import useSearchScrollLock from './useSearchScrollLock'
import { pageBuilderOutlinedButtonSx } from 'src/theme/pageBuilderButtons'
import { useDispatch } from 'src/redux/hooks'

/* Material */
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha, Theme } from '@mui/material/styles'

/* Material-Icons */
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

/* Types */
import { item, tMenu } from 'src/shared/frontendTypes/navigation'
import { tProductPreview } from 'src/shared/frontendTypes/product'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'

/* Functions */
import { formatSearchInput as formatSucheInput, saveSearchQuery as saveSuche } from 'src/functions/searchInput'
import { joinSearchRequest, peekSearchRequest, requestSearch, searchPreviewRequest, searchListingRequest } from 'src/functions/searchRequests'
import getImageUrl from 'src/pages/Product/functions/imageUrl'
import getImageAlt from 'src/pages/Product/functions/imageAlt'

/* Components */
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import LoadingIcon from 'src/components/LoadingIcon'

/* Config */
import config from 'src/config'
import { hrefForBrandPath, hrefForNavigationItem } from 'src/functions/navigationBreadcrumbs'
import productUrl, { productVariantUrl } from 'src/functions/productUrl'
import useNavigationPaths from 'src/layout/navigationPaths'
import { resolvePageBuilder } from 'src/pageBuilder/config'
import { isFilterProductsResponse, isSearchPreviewResponse } from 'src/shared/frontendTypes/productsFilter'
import type { brandSuggestion as tBrandSuggestion } from 'src/shared/frontendTypes/productsFilter'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

interface Props {
  open: boolean
  pageBuilder?: MenuPageBuilder
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

interface SearchCategory {
  label: string
  href: string
  imageSrc?: string
  imageAlt?: string
  type: 'category' | 'brand'
}

const getEntryHref = (entry: item) => {
  return hrefForNavigationItem(entry) || '#'
}

const dedupeCategories = (items: SearchCategory[]) => {
  const seen = new Set<string>()

  return items.filter(item => {
    const key = `${item.label}-${item.href}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const getSearchCategories = (
  pageBuilder: MenuPageBuilder | undefined,
  paths: tMenu
): SearchCategory[] => {
  const builderSearchItems = resolvePageBuilder(pageBuilder).search.items || []
  if (builderSearchItems.length > 0) {
    return dedupeCategories(builderSearchItems.map(item => ({
      label: item.label,
      href: item.href,
      imageSrc: item.imageSrc,
      imageAlt: item.imageAlt,
      type: item.href.startsWith('/b/') ? 'brand' as const : 'category' as const,
    }))).slice(0, 12)
  }

  const fromBuilder = resolvePageBuilder(pageBuilder).blocks.reduce<SearchCategory[]>((acc, block) => {
    if (block.type === 'categoryList' || block.type === 'categorySlider') {
      acc.push(...block.items.map(item => ({
        label: item.label,
        href: item.href,
        imageSrc: item.imageSrc,
        imageAlt: item.imageAlt,
        type: 'category' as const,
      })))

      return acc
    }

    if (block.type === 'brandList') {
      acc.push(...(block.items || []).map(item => ({
        label: item.label,
        href: item.href,
        imageSrc: item.imageSrc,
        imageAlt: item.imageAlt,
        type: 'brand' as const,
      })))

      return acc
    }

    return acc
  }, [])

  if (fromBuilder.length > 0) return dedupeCategories(fromBuilder).slice(0, 8)

  return dedupeCategories(paths
    .map(entry => ({
      label: entry.title,
      href: getEntryHref(entry),
      type: entry.brand ? 'brand' as const : 'category' as const,
    }))
    .filter(entry => entry.href !== '#')
  ).slice(0, 8)
}

const formatPrice = (value: number) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(value)

const getDisplayPrice = (product: tProductPreview) =>
  typeof product.discount?.price === 'number'
    ? product.discount.price
    : product.price

const getSearchProductUrl = (product: tProductPreview) => {
  const href = productUrl(product.id, product.path)
  const combinationKey = String(product.key || '').trim()

  if (combinationKey && combinationKey !== String(product.id)) {
    return productVariantUrl(product.id, product.path, combinationKey)
  }

  if (product.options && product.options.length > 0) {
    return productUrl(product.id, product.path, product.options)
  }

  return href
}

const getBrandLogoUrl = (logo?: string) => {
  if (!logo) return ''
  if (/^https?:\/\//i.test(logo)) return logo
  if (logo.startsWith('/')) return logo

  return `${config.image_server}/brand/${logo}`
}

const getBrowseTitle = (
  showCategories: boolean,
  showBrands: boolean
) => {
  if (showCategories && showBrands) return 'Stöbere in Kategorien & Marken'
  if (showBrands) return 'Stöbere in unseren Marken'
  return 'Stöbere in unseren Kategorien'
}

const Suche: React.FC<Props> = ({ open, pageBuilder, landingPageImageAiGenerated }) => {
  const router = useRouter()
  const [dialog, setDialog] = useState<HTMLDivElement | null>(null)
  const releaseScrollLock = useSearchScrollLock(open, dialog)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const paths = useNavigationPaths()
  const builderConfig = useMemo(
    () => resolvePageBuilder(pageBuilder),
    [pageBuilder]
  )
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated],
  )

  const { propsSuche, closeSearchOverlay } = SucheRedux()
  const dispatch = useDispatch()

  const [query, setQuery] = useState(propsSuche || '')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<tProductPreview[]>([])
  const [resultQuery, setResultQuery] = useState('')
  const completedQuery = useRef('')
  const prefetchedProductRoutes = useRef(new Set<string>())
  const [brandSuggestion, setBrandSuggestion] = useState<tBrandSuggestion | null>(null)
  const [hasRequested, setHasRequested] = useState(false)
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([])
  const [searchError, setSearchError] = useState(false)
  const savedQuery = saveSuche(query)
  const normalizedQuery = savedQuery.toLocaleLowerCase('de')
  const normalizedResultQuery = resultQuery.toLocaleLowerCase('de')
  const showResults = results.length > 0 && Boolean(normalizedResultQuery)
    && (normalizedQuery === normalizedResultQuery || normalizedQuery.startsWith(normalizedResultQuery))
  const showingPreviousResults = showResults && normalizedQuery !== normalizedResultQuery

  const categories = useMemo(
    () => getSearchCategories(pageBuilder, paths),
    [pageBuilder, paths]
  )
  const browseItems = useMemo(
    () => categories.filter(item =>
      item.type === 'category'
        ? builderConfig.search.showCategories
        : builderConfig.search.showBrands
    ),
    [builderConfig.search.showBrands, builderConfig.search.showCategories, categories]
  )

  const popularTerms = useMemo(
    () => browseItems.slice(0, 5).map(item => item.label),
    [browseItems]
  )

  useEffect(() => {
    if (!open || !dialog) return
    const viewport = window.visualViewport
    // The keyboard can shrink the visible viewport without resizing the page.
    // Resize only the dialog shell; typing must not move its search header.
    const updateViewport = () => {
      dialog.style.setProperty('--search-viewport-height', `${viewport?.height ?? window.innerHeight}px`)
      dialog.style.setProperty('--search-viewport-top', `${viewport?.offsetTop ?? 0}px`)
    }
    updateViewport()
    viewport?.addEventListener('resize', updateViewport)
    viewport?.addEventListener('scroll', updateViewport)
    window.addEventListener('resize', updateViewport)
    return () => {
      viewport?.removeEventListener('resize', updateViewport)
      viewport?.removeEventListener('scroll', updateViewport)
      window.removeEventListener('resize', updateViewport)
    }
  }, [dialog, open])

  useEffect(() => {
    if (!open || typeof window === 'undefined') return
    // Warm the result page's code and static props while the user is typing.
    // This does not start a product search, and failure must not block Enter.
    void router.prefetch('/suche').catch(() => {})
  }, [open, router])

  useEffect(() => {
    if (!open) return

    setQuery(propsSuche || '')
    setResults([])
    setResultQuery('')
    completedQuery.current = ''
    prefetchedProductRoutes.current.clear()
    setBrandSuggestion(null)
    setLoading(false)
    setHasRequested(false)
  }, [open, propsSuche])

  useEffect(() => {
    if (!open) return

    const savedQuery = saveSuche(query)
    const previous = completedQuery.current.toLocaleLowerCase('de')
    const next = savedQuery.toLocaleLowerCase('de')
    // Keep a clearly labelled previous preview only while extending its text.
    // It is never cached or counted as the result of the new query.
    if (!previous || (next !== previous && !next.startsWith(previous))) {
      setResults([])
      setResultQuery('')
      completedQuery.current = ''
    }
    setBrandSuggestion(null)
    setSearchSuggestions([])
    setSearchError(false)
    setHasRequested(false)
    if (savedQuery.length < 2 || !builderConfig.search.showProducts) {
      setLoading(false)
      setHasRequested(savedQuery.length >= 2)
      return
    }

    let active = true
    let subscription: ReturnType<typeof requestSearch> | undefined
    const body = searchPreviewRequest(config.shop, savedQuery)
    const apply = (data: import('src/shared/frontendTypes/productsFilter').searchPreviewResponse) => {
      if (!active) return
      setResults(data.filterProducts.products)
      setResultQuery(savedQuery)
      completedQuery.current = savedQuery
      setBrandSuggestion(data.brandSuggestion || null)
      setSearchSuggestions(data.searchSuggestions || [])
      setHasRequested(true)
      setLoading(false)
    }
    const cached = peekSearchRequest('products', body, isSearchPreviewResponse)
    if (cached) {
      apply(cached)
      return () => { active = false }
    }
    setLoading(true)
    const timeout = window.setTimeout(() => {
      const request = requestSearch('products', body, isSearchPreviewResponse)
      subscription = request
      request.promise.then(apply).catch(() => {
        if (!active) return
        setSearchError(true)
        setLoading(false)
      })
    }, 240)

    return () => {
      active = false
      window.clearTimeout(timeout)
      subscription?.release()
    }
  }, [builderConfig.search.showProducts, open, query])

  useEffect(() => {
    if (!open || loading || !showResults || showingPreviousResults) return
    // Next 13's guarded PageLoader API warms only the shared JS/CSS, never
    // router.prefetch(href): for ISR pages that also fetches full product data.
    const routes = new Set(results.slice(0, 4).map(product => {
      const key = String(product.key || '').trim()
      return key && key !== String(product.id)
        ? '/p/[alias]/[pid]/[variant]'
        : '/p/[alias]/[pid]'
    }))
    const timeout = window.setTimeout(() => {
      const loader = Router.router?.pageLoader
      if (!loader?.prefetch) return
      for (const route of routes) {
        if (prefetchedProductRoutes.current.has(route)) continue
        prefetchedProductRoutes.current.add(route)
        try {
          void loader.prefetch(route).catch(() => prefetchedProductRoutes.current.delete(route))
        } catch {
          prefetchedProductRoutes.current.delete(route)
        }
      }
    }, 120)
    return () => window.clearTimeout(timeout)
  }, [loading, open, results, showResults, showingPreviousResults])

  const handleClose = () => {
    releaseScrollLock()
    closeSearchOverlay()
  }

  const submitSearch = (nextQuery = query) => {
    const savedQuery = saveSuche(nextQuery)
    // Keep a pending preview alive across route loading, and begin the exact
    // first-page request now. The destination subscribes to the same promise.
    const preview = joinSearchRequest('products', searchPreviewRequest(config.shop, savedQuery))
    preview?.promise.catch(() => {}).finally(() => preview.release())
    const listing = requestSearch('products', searchListingRequest(config.shop, savedQuery), isFilterProductsResponse)
    listing.promise.catch(() => {}).finally(() => listing.release())
    if (savedQuery === propsSuche) dispatch({ type: 'resetFilter' })
    handleClose()

    if (!savedQuery) {
      router.push('/suche')
      return
    }

    router.push({
      pathname: '/suche',
      query: { suche: savedQuery },
    })
  }

  const openProduct = (product: tProductPreview) => {
    const href = getSearchProductUrl(product)
    handleClose()
    router.push(href)
  }

  const openCategory = (href: string) => {
    handleClose()
    router.push(href)
  }

  const openBrandSuggestion = (brand: tBrandSuggestion) => {
    handleClose()
    router.push(hrefForBrandPath(brand.path, brand.id) || '/brands')
  }

  const renderCategoryGrid = () =>
    <Box
      sx={{
        display: 'grid',
        gap: isMobile ? 1.5 : 2,
        gridTemplateColumns: isMobile
          ? 'repeat(2, minmax(0, 1fr))'
          : 'repeat(4, minmax(0, 1fr))',
      }}
    >
      {browseItems.map(category => <Box
        key={`${category.label}-${category.href}`}
        component='button'
        type='button'
        onClick={() => openCategory(category.href)}
        sx={{
          position: 'relative',
          border: 'none',
          padding: 0,
          overflow: 'hidden',
          borderRadius: 3,
          background: alpha('#ffffff', 0.7),
          cursor: 'pointer',
          textAlign: 'left',
          minHeight: isMobile ? 180 : 220,
          boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)',
        }}
      >
        {category.imageSrc
          ? <>
              <Box
                component='img'
                src={category.imageSrc}
                alt={category.imageAlt || category.label}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <AiGeneratedBadge
                aiGenerated={isAiGeneratedLandingImage(category.imageSrc)}
                size='compact'
                placement='top-left'
              />
            </>
          : <Box
              sx={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(242, 235, 227, 0.95))',
              }}
            />
        }

        <Box
          sx={{
            position: 'absolute',
            left: 14,
            right: 14,
            bottom: 14,
            display: 'inline-flex',
            justifyContent: 'center',
            borderRadius: 999,
            padding: '10px 16px',
            background: alpha('#ffffff', 0.92),
            backdropFilter: 'blur(14px)',
            color: 'var(--page-builder-nav-text, #1f2933)',
            fontSize: isMobile ? 16 : 18,
            fontWeight: 500,
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
          }}
        >
          {category.label}
        </Box>
      </Box>)}
    </Box>

  const renderResults = () =>
    <Box sx={{ display: 'grid', gap: 2 }}>
      {results.map(product => <Box
        key={product.key}
        component='a'
        href={getSearchProductUrl(product)}
        aria-label={`Zur Produktseite: ${product.name}`}
        onClick={event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
          event.preventDefault()
          openProduct(product)
        }}
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '88px 1fr auto' : '108px 1fr auto',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          border: 'none',
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
          background: 'transparent',
          padding: 0,
          paddingBottom: 2,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: isMobile ? 88 : 108,
            height: isMobile ? 88 : 108,
          }}
        >
          <Box
            component='img'
            src={getImageUrl(product.images?.[0], product.id, 'sm') || ''}
            alt={getImageAlt(product.images?.[0], product.metaTitle)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: 2,
              background: '#f8f6f2',
              padding: 1,
            }}
          />
          <AiGeneratedBadge aiGenerated={product.images?.[0]?.aiGenerated} size='compact' />
        </Box>

        <Box sx={{ display: 'grid', gap: .75 }}>
          <Typography sx={{ fontSize: isMobile ? 20 : 24, fontWeight: 500 }}>
            {product.name}
          </Typography>
          <Typography sx={{ color: 'var(--page-builder-muted, #667085)', fontSize: isMobile ? 16 : 18 }}>
            {formatPrice(getDisplayPrice(product))}
          </Typography>
        </Box>

        <Box
          component='span'
          aria-hidden='true'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            width: 44,
            height: 44,
            background: '#f1f1f1',
            color: 'var(--page-builder-nav-text, #1f2933)',
          }}
        >
          <ArrowForwardIcon />
        </Box>
      </Box>)}
    </Box>

  const renderBrandSuggestion = (brand: tBrandSuggestion) =>
    <Box
      component='button'
      type='button'
      onClick={() => openBrandSuggestion(brand)}
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '88px 1fr auto' : '108px 1fr auto',
        gap: 2,
        alignItems: 'center',
        width: '100%',
        border: '1px solid rgba(15, 23, 42, 0.10)',
        borderRadius: 2,
        background: alpha('#ffffff', 0.72),
        padding: isMobile ? 1.5 : 2,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          width: isMobile ? 88 : 108,
          height: isMobile ? 72 : 86,
          borderRadius: 2,
          background: '#f8f6f2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {brand.logo
          ? <>
            <Box
              component='img'
              src={getBrandLogoUrl(brand.logo)}
              alt={brand.titel}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                padding: 1,
              }}
            />
            <AiGeneratedBadge
              aiGenerated={isAiGeneratedLandingImage(getBrandLogoUrl(brand.logo))}
              size='compact'
              placement='top-left'
            />
          </>
          : <Typography sx={{ fontSize: 22, fontWeight: 500 }}>
              {brand.titel.slice(0, 1)}
            </Typography>
        }
      </Box>

      <Box sx={{ display: 'grid', gap: .5 }}>
        <Typography sx={{ fontSize: isMobile ? 16 : 18, color: 'var(--page-builder-muted, #667085)' }}>
          Marke
        </Typography>
        <Typography sx={{ fontSize: isMobile ? 22 : 26, fontWeight: 500 }}>
          {brand.titel}
        </Typography>
      </Box>

      <IconButton
        aria-label='Marke öffnen'
        sx={{
          width: 44,
          height: 44,
          background: '#f1f1f1',
          color: 'var(--page-builder-nav-text, #1f2933)',
        }}
      >
        <ArrowForwardIcon />
      </IconButton>
    </Box>

  return <Dialog
    ref={setDialog}
    disableScrollLock
    open={open}
    onClose={handleClose}
    fullWidth
    maxWidth='lg'
    sx={{
      top: 'var(--search-viewport-top, 0px)',
      height: 'var(--search-viewport-height, 100%)',
      bottom: 'auto',
      '& .MuiDialog-container': { alignItems: 'flex-start' },
    }}
    PaperProps={{
      sx: {
        width: 'min(1120px, calc(100% - 24px))',
        margin: isMobile ? '12px' : '28px auto',
        height: isMobile ? 'calc(100% - 24px)' : 'min(720px, calc(100% - 56px))',
        maxHeight: isMobile ? 'calc(100% - 24px)' : 'calc(100% - 56px)',
        overflow: 'hidden',
        borderRadius: isMobile ? 3 : 4,
        background: alpha('#ffffff', 0.92),
        backdropFilter: 'blur(22px)',
        boxShadow: '0 32px 90px rgba(15, 23, 42, 0.18)',
      },
    }}
    BackdropProps={{
      sx: {
        backgroundColor: 'rgba(15, 23, 42, 0.2)',
        backdropFilter: 'blur(10px)',
      },
    }}
  >
    <Box sx={{ p: isMobile ? 2 : 3, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
          alignItems: 'center',
          flexShrink: 0,
          gap: 1.5,
          pb: 2,
        }}
      >
        <SearchIcon sx={{ color: 'var(--page-builder-muted, #667085)', fontSize: isMobile ? 28 : 34 }} />

        <InputBase
          value={query}
          autoFocus
          placeholder='Suchen'
          onChange={({ target: { value } }) => setQuery(formatSucheInput(value))}
          onKeyDown={event => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            submitSearch()
          }}
          sx={{
            minWidth: 0,
            fontSize: isMobile ? 22 : 28,
            lineHeight: 1.2,
          }}
        />

        <IconButton
          aria-label='Suche öffnen'
          onClick={() => submitSearch()}
          sx={{ color: 'var(--page-builder-muted, #667085)' }}
        >
          <ArrowForwardIcon />
        </IconButton>

        <IconButton
          aria-label='Suche schließen'
          onClick={handleClose}
          sx={{ color: 'var(--page-builder-muted, #667085)' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      <Box
        data-search-scroll-area
        sx={{
          pt: isMobile ? 2 : 3,
          pb: 1,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehaviorY: 'contain',
          WebkitOverflowScrolling: 'touch',
          display: 'grid',
          alignContent: 'start',
          gap: isMobile ? 3 : 4,
        }}
      >
        {saveSuche(query).length < 2 &&
          <>
            {popularTerms.length > 0 &&
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Typography
                  sx={{
                    color: 'var(--page-builder-muted, #667085)',
                    fontSize: isMobile ? 16 : 18,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  Beliebte Suchbegriffe
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {popularTerms.map(term => <Chip
                    key={term}
                    label={term}
                    onClick={() => setQuery(term)}
                    sx={{
                      height: isMobile ? 44 : 50,
                      borderRadius: 999,
                      background: '#f2f2f2',
                      fontSize: isMobile ? 18 : 20,
                    }}
                  />)}
                </Box>
              </Box>
            }

            {browseItems.length > 0 &&
              <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Typography
                sx={{
                  color: 'var(--page-builder-muted, #667085)',
                  fontSize: isMobile ? 16 : 18,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                }}
              >
                {getBrowseTitle(builderConfig.search.showCategories, builderConfig.search.showBrands)}
              </Typography>

              {renderCategoryGrid()}
              </Box>
            }
          </>
        }

        {saveSuche(query).length >= 2 &&
          <>
            {loading &&
              <Box
                sx={{
                  minHeight: showResults ? 32 : 180,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--page-builder-muted, #667085)',
                }}
              >
                <Box role='status' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LoadingIcon />
                  {showResults && <span>Suche wird aktualisiert …</span>}
                </Box>
              </Box>
            }

            {!loading && brandSuggestion &&
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Typography
                  sx={{
                    color: 'var(--page-builder-muted, #667085)',
                    fontSize: isMobile ? 16 : 18,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  Marke gefunden
                </Typography>

                {renderBrandSuggestion(brandSuggestion)}
              </Box>
            }

            {builderConfig.search.showProducts && showResults &&
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Typography
                  sx={{
                    color: 'var(--page-builder-muted, #667085)',
                    fontSize: isMobile ? 16 : 18,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  {showingPreviousResults ? `Bisherige Ergebnisse für „${resultQuery}“` : 'Passende Produkte'}
                </Typography>

                {renderResults()}
              </Box>
            }

            {!loading && searchError && <Typography role='alert'>Die Vorschau konnte nicht geladen werden. Sie können die Suche trotzdem öffnen.</Typography>}

            {!loading && hasRequested && !brandSuggestion && (!builderConfig.search.showProducts || results.length === 0) &&
              <>
                <Typography
                  sx={{
                    color: 'var(--page-builder-muted, #667085)',
                    fontSize: isMobile ? 20 : 24,
                    textAlign: 'center',
                  }}
                >
                  Leider konnten wir keine Ergebnisse für &quot;{saveSuche(query)}&quot; finden.
                </Typography>

                {searchSuggestions.length > 0 && <Box sx={{ textAlign: 'center' }}>
                  <Typography>Meintest du …?</Typography>
                  {searchSuggestions.map(suggestion => <Chip
                    key={suggestion}
                    label={suggestion}
                    clickable
                    sx={{ m: 0.5 }}
                    onClick={() => submitSearch(suggestion)}
                  />)}
                </Box>}

                {browseItems.length > 0 &&
                  <Box sx={{ display: 'grid', gap: 1.5 }}>
                  <Typography
                    sx={{
                      color: 'var(--page-builder-muted, #667085)',
                      fontSize: isMobile ? 16 : 18,
                      letterSpacing: '.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {getBrowseTitle(builderConfig.search.showCategories, builderConfig.search.showBrands)}
                  </Typography>

                  {renderCategoryGrid()}
                  </Box>
                }
              </>
            }

            <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
              <Button
                component='a'
                href={`/suche?suche=${encodeURIComponent(savedQuery)}`}
                variant='outlined'
                endIcon={<ArrowForwardIcon />}
                sx={pageBuilderOutlinedButtonSx}
                onClick={event => {
                  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
                  event.preventDefault()
                  submitSearch()
                }}
              >
                Alle Suchergebnisse anzeigen
              </Button>
            </Box>
          </>
        }
      </Box>
    </Box>
  </Dialog>
}

export default Suche
